import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { fmtMoney } from "@/lib/money";
import {
  ShieldCheck, Percent, ShoppingCart, RotateCcw, Book, Package,
  CheckCircle2, XCircle, Clock, RefreshCw, Settings as SettingsIcon,
  ChevronRight, AlertTriangle, X,
} from "lucide-react";

const KIND_ICON = {
  discount:         Percent,
  purchase_order:   ShoppingCart,
  refund:           RotateCcw,
  manual_journal:   Book,
  inventory_adjust: Package,
};

const KIND_LABEL = {
  discount:         "Discount",
  purchase_order:   "Purchase Order",
  refund:           "Refund",
  manual_journal:   "Manual Journal",
  inventory_adjust: "Inventory Adjustment",
};

const KIND_LINK = {
  discount:         (r) => `/admin/estimates/${r.ref_id}`,
  purchase_order:   () => `/admin/purchase-orders`,
  manual_journal:   () => `/admin/journal-entries`,
  refund:           () => `/admin/payments`,
  inventory_adjust: () => `/admin/materials`,
};

function StatusPill({ status }) {
  const tones = {
    pending:   "bg-amber-100 text-amber-700",
    approved:  "bg-emerald-100 text-emerald-700",
    rejected:  "bg-red-100 text-red-700",
    cancelled: "bg-slate-200 text-slate-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${tones[status] || tones.pending}`}>
      {status}
    </span>
  );
}

function ImpactPreview({ impact, kind }) {
  if (!impact) return null;
  if (kind === "discount") {
    return (
      <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-[#E2E8F0] dark:border-slate-700 p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Original</div>
          <div className="font-semibold text-[#0A2540] dark:text-white">{fmtMoney(impact.before_total || 0)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Discount</div>
          <div className="font-semibold text-amber-600">-{fmtMoney(impact.discount_amt || 0)} ({(impact.discount_pct || 0).toFixed(1)}%)</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">New total</div>
          <div className="font-semibold text-[#0A2540] dark:text-white">{fmtMoney(impact.after_total || 0)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Δ Revenue</div>
          <div className="font-semibold text-red-600">-{fmtMoney((impact.before_total || 0) - (impact.after_total || 0))}</div>
        </div>
      </div>
    );
  }
  return null;
}

function ApprovalRow({ row, onDecide, deciding }) {
  const Icon = KIND_ICON[row.kind] || ShieldCheck;
  const linkTo = KIND_LINK[row.kind]?.(row);
  const isPending = row.status === "pending";
  return (
    <div className="border-b border-[#E2E8F0] dark:border-slate-700 last:border-b-0" data-testid={`approval-${row.approval_id}`}>
      <div className="px-5 py-4 flex flex-col md:flex-row md:items-start gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="w-10 h-10 rounded-xl bg-[#C9A356]/15 grid place-items-center text-[#C9A356] shrink-0">
            <Icon className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">{KIND_LABEL[row.kind] || row.kind}</span>
              <StatusPill status={row.status} />
              {row.virtual && <span className="text-[10px] text-[#94A3B8]">(from estimate)</span>}
            </div>
            <div className="text-sm text-[#0A2540] dark:text-white mt-1 font-medium truncate">
              {row.reason || row.ref_id}
            </div>
            <div className="text-xs text-[#64748B] mt-1">
              {row.kind === "discount" ? (
                <>Estimate <span className="font-mono">{row.ref_id}</span></>
              ) : (
                <>Amount <span className="font-semibold text-[#0A2540] dark:text-white">{fmtMoney(row.amount || 0)}</span></>
              )}
              {row.threshold_at_request > 0 && ` · threshold ${row.kind === "discount" ? `${row.threshold_at_request}%` : fmtMoney(row.threshold_at_request)}`}
              {row.requested_by_name && ` · by ${row.requested_by_name}`}
              {row.created_at && ` · ${new Date(row.created_at).toLocaleDateString()}`}
            </div>
            {row.decision_note && (
              <div className="text-xs text-[#64748B] mt-1 italic">Note: {row.decision_note}</div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {linkTo && (
            <Link to={linkTo} className="text-xs text-[#0A2540] dark:text-white hover:underline inline-flex items-center gap-1" data-testid={`link-${row.approval_id}`}>
              View <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          )}
          {isPending && !row.virtual && (
            <>
              <button
                disabled={deciding}
                onClick={() => onDecide(row, "reject")}
                data-testid={`reject-${row.approval_id}`}
                className="px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
              >
                <XCircle className="w-3.5 h-3.5" /> Reject
              </button>
              <button
                disabled={deciding}
                onClick={() => onDecide(row, "approve")}
                data-testid={`approve-${row.approval_id}`}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Approve
              </button>
            </>
          )}
          {isPending && row.virtual && (
            <Link
              to={KIND_LINK[row.kind]?.(row) || "#"}
              className="px-3 py-1.5 rounded-lg bg-[#0A2540] text-white text-xs font-semibold inline-flex items-center gap-1"
              data-testid={`open-source-${row.approval_id}`}
            >
              Decide on quote <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
      {row.financial_impact && (
        <div className="px-5 pb-4">
          <ImpactPreview impact={row.financial_impact} kind={row.kind} />
        </div>
      )}
    </div>
  );
}

function SettingsModal({ open, onClose, settings, onSave }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(settings || {}); }, [settings, open]);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { k: "discount_pct_threshold",     label: "Discount %",              sub: "Quotes with ≥ this % discount need approval", unit: "%" },
    { k: "purchase_order_threshold",   label: "Purchase Order",          sub: "POs at/above this total require approval before sending", unit: "$" },
    { k: "refund_threshold",           label: "Refund",                  sub: "Refunds at/above this amount require approval",           unit: "$" },
    { k: "manual_journal_threshold",   label: "Manual Journal Entry",    sub: "Manual JEs at/above this total require approval",         unit: "$" },
    { k: "inventory_adjust_threshold", label: "Inventory Adjustment",    sub: "Stock adjustments at/above this qty require approval",   unit: "qty" },
  ];

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 dark:border dark:border-slate-700 rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="approval-settings-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700">
          <h3 className="font-heading font-semibold text-[#0A2540] dark:text-white">Approval thresholds</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0A2540]"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {fields.map((f) => (
            <div key={f.k}>
              <label className="text-sm font-medium text-[#0A2540] dark:text-white">{f.label}</label>
              <div className="text-xs text-[#64748B] mb-1">{f.sub}</div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#64748B] w-6 text-right">{f.unit}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form[f.k] ?? 0}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  data-testid={`threshold-${f.k}`}
                  className="flex-1 px-3 py-2 rounded-lg border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
                />
              </div>
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-[#0A2540] dark:text-white">Cancel</button>
            <button type="submit" disabled={saving} data-testid="save-thresholds" className="px-4 py-2 rounded-lg bg-[#0A2540] text-white text-sm font-semibold disabled:opacity-50">
              {saving ? "Saving…" : "Save thresholds"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminApprovals() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async (t = tab) => {
    setRefreshing(true);
    try {
      const [rL, rS, rSet] = await Promise.all([
        api.get("/admin/approvals", { params: { status: t } }),
        api.get("/admin/approvals/summary"),
        api.get("/admin/approval-settings"),
      ]);
      setRows(rL.data);
      setSummary(rS.data);
      setSettings(rSet.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load approvals");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tab]);

  useEffect(() => { load(tab); }, [load, tab]);

  const onDecide = async (row, action) => {
    const note = window.prompt(`Add an optional note for this ${action}:`) || "";
    setDeciding(true);
    try {
      await api.post(`/admin/approvals/${row.approval_id}/${action}`, { note });
      toast.success(`Approval ${action === "approve" ? "approved" : "rejected"}`);
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setDeciding(false);
    }
  };

  const saveSettings = async (form) => {
    try {
      const payload = {};
      for (const k of Object.keys(form)) {
        const v = Number(form[k]);
        if (!isNaN(v) && v >= 0) payload[k] = v;
      }
      const r = await api.patch("/admin/approval-settings", payload);
      setSettings(r.data);
      toast.success("Thresholds updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  if (loading) return <div className="text-sm text-[#94A3B8]">Loading approvals…</div>;

  const pendingTotal = summary?.by_status?.pending || 0;

  return (
    <div data-testid="admin-approvals" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-[#0A2540] dark:text-white">Approvals</h1>
            {pendingTotal > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                <Clock className="w-3.5 h-3.5" /> {pendingTotal} PENDING
              </span>
            )}
          </div>
          <p className="text-sm text-[#64748B] dark:text-slate-400 mt-1">
            Every consequential action above threshold routes here. Approve, reject, or open the source record.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSettingsOpen(true)}
            data-testid="btn-thresholds"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-[#E2E8F0] dark:border-slate-700 text-sm text-[#0A2540] dark:text-white font-semibold hover:bg-slate-50 dark:hover:bg-slate-800/60"
          >
            <SettingsIcon className="w-4 h-4" /> Thresholds
          </button>
          <button
            onClick={() => load(tab)}
            disabled={refreshing}
            data-testid="btn-refresh-approvals"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0A2540] text-white text-sm font-semibold hover:brightness-110 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Kind breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(KIND_LABEL).map(([kind, label]) => {
          const Icon = KIND_ICON[kind];
          const count = summary?.pending_by_kind?.[kind] || 0;
          return (
            <div key={kind} className="card-pb p-4" data-testid={`kpi-${kind}`}>
              <div className="flex items-center justify-between">
                <span className="w-8 h-8 rounded-lg bg-[#C9A356]/15 grid place-items-center text-[#C9A356]">
                  <Icon className="w-4 h-4" />
                </span>
                <span className={`text-3xl font-heading font-semibold ${count > 0 ? "text-amber-600" : "text-[#94A3B8]"}`}>
                  {count}
                </span>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mt-2">{label}</div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#E2E8F0] dark:border-slate-700">
        {["pending", "approved", "rejected", "cancelled"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            className={`px-4 py-2 text-sm font-semibold capitalize border-b-2 -mb-px ${tab === t ? "border-[#C9A356] text-[#0A2540] dark:text-white" : "border-transparent text-[#64748B] hover:text-[#0A2540]"}`}
          >
            {t} {tab === t && summary && ` · ${summary.by_status?.[t] || 0}`}
          </button>
        ))}
      </div>

      {/* Rows */}
      <div className="card-pb overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#94A3B8]">
            {tab === "pending" ? (
              <div className="flex flex-col items-center gap-2">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                <div className="text-[#0A2540] dark:text-white font-medium">Nothing pending — inbox zero.</div>
              </div>
            ) : (
              <>No {tab} approvals</>
            )}
          </div>
        ) : (
          rows.map((row) => (
            <ApprovalRow key={row.approval_id} row={row} onDecide={onDecide} deciding={deciding} />
          ))
        )}
      </div>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} settings={settings} onSave={saveSettings} />
    </div>
  );
}
